import React from 'react';
import { Droplet, DollarSign, Gauge, Clock, Car, Truck, Leaf, Ruler } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета расхода топлива
 */
const ResultsOverview = ({ results, formData, selectedUnit, setSelectedUnit }) => {
  if (!results) return null;

  // Получение единицы измерения для расхода
  const getConsumptionUnit = () => {
    const unit = formData.fuelType === 'electricity' ? 'кВт·ч/100 км' : 'л/100 км';
    return unit;
  };

  // Получение единицы измерения для количества топлива
  const getFuelAmountUnit = () => {
    return formData.fuelType === 'electricity' ? 'кВт·ч' : 'л';
  };

  return (
    <ToolResult title="Результаты расчета расхода топлива">
      {/* Переключатель единиц отображения */}
      <div className="grid grid-cols-3 gap-1 mb-5 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full sm:w-auto">
        <button
          className={`px-3 py-2 text-sm font-medium rounded transition ${
            selectedUnit === 'consumption'
              ? 'bg-white dark:bg-gray-700 shadow text-blue-600 dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
          onClick={() => setSelectedUnit('consumption')}
        >
          Расход
        </button>
        <button
          className={`px-3 py-2 text-sm font-medium rounded transition ${
            selectedUnit === 'cost'
              ? 'bg-white dark:bg-gray-700 shadow text-blue-600 dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
          onClick={() => setSelectedUnit('cost')}
        >
          Стоимость
        </button>
        <button
          className={`px-3 py-2 text-sm font-medium rounded transition ${
            selectedUnit === 'eco'
              ? 'bg-white dark:bg-gray-700 shadow text-blue-600 dark:text-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
          onClick={() => setSelectedUnit('eco')}
        >
          Экология
        </button>
      </div>

      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {selectedUnit === 'consumption' && (
          <>
            {/* Расход топлива */}
            <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
              <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
                <Droplet className="w-4 h-4"/>
                Расход топлива
              </h3>
              <div className="flex items-baseline flex-wrap gap-x-2">
                <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
                  {formatNumber(results.fuelConsumptionWithEco, 1)}
                </span>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {getConsumptionUnit()}
                </span>
              </div>
              {results.ecoSavingsLiters > 0 && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center">
                  <Leaf className="w-3 h-3 mr-1 text-green-500" />
                  Экономия: {formatNumber(results.ecoSavingsLiters, 1)} л/100км ({formatNumber(results.ecoSavingsPercent, 0)}%)
                </p>
              )}
            </div>

            {/* Расход топлива на поездку */}
            <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
              <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
                <Truck className="w-4 h-4"/>
                Топливо на поездку
              </h3>
              <div className="flex items-baseline flex-wrap gap-x-2">
                <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
                  {formatNumber(results.fuelAmount, 1)}
                </span>
                <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                  {getFuelAmountUnit()}
                </span>
              </div>
              {results.fuelType && (
                <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                  {results.fuelType.name}
                </p>
              )}
            </div>
          </>
        )}

        {selectedUnit === 'cost' && (
          <>
            {/* Стоимость топлива */}
            <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/30 dark:to-green-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
              <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
                <DollarSign className="w-4 h-4"/>
                Стоимость топлива
              </h3>
              <div className="flex items-baseline flex-wrap gap-x-2">
                <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
                  {formatCurrency(results.fuelCost, 0)}
                </span>
              </div>
              <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                {formatNumber(results.costPer100km, 1)} ₽/100км ({formatNumber(results.costPerKm, 2)} ₽/км)
              </p>
            </div>

            {/* Общая стоимость поездки */}
            <div className="rounded-lg bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/30 dark:to-cyan-900/30 p-5 border border-teal-100 dark:border-teal-800/50 shadow-sm">
              <h3 className="text-base font-medium text-teal-700 dark:text-teal-300 mb-1.5 flex items-center gap-1.5">
                <DollarSign className="w-4 h-4"/>
                Общая стоимость поездки
              </h3>
              <div className="flex items-baseline flex-wrap gap-x-2">
                <span className="text-3xl font-bold text-teal-800 dark:text-teal-200" aria-live="polite">
                  {formatCurrency(results.totalTripCost, 0)}
                </span>
              </div>
              {results.additionalCosts > 0 && (
                <p className="text-xs text-teal-600 dark:text-teal-400 mt-1">
                  Включая дополнительные расходы: {formatCurrency(results.additionalCosts, 0)}
                </p>
              )}
            </div>
          </>
        )}

        {selectedUnit === 'eco' && (
          <>
            {/* Выбросы CO2 */}
            <div className="rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 p-5 border border-amber-100 dark:border-amber-800/50 shadow-sm">
              <h3 className="text-base font-medium text-amber-700 dark:text-amber-300 mb-1.5 flex items-center gap-1.5">
                <Leaf className="w-4 h-4"/>
                Выбросы CO₂
              </h3>
              <div className="flex items-baseline flex-wrap gap-x-2">
                <span className="text-3xl font-bold text-amber-800 dark:text-amber-200" aria-live="polite">
                  {formatNumber(results.co2Emissions, 1)}
                </span>
                <span className="text-sm font-medium text-amber-600 dark:text-amber-400">
                  кг
                </span>
              </div>
              <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                {formatNumber(results.co2EmissionsPerKm, 2)} кг CO₂/км
              </p>
            </div>

            {/* Экологический след */}
            <div className="rounded-lg bg-gradient-to-br from-lime-50 to-green-50 dark:from-lime-900/30 dark:to-green-900/30 p-5 border border-lime-100 dark:border-lime-800/50 shadow-sm">
              <h3 className="text-base font-medium text-lime-700 dark:text-lime-300 mb-1.5 flex items-center gap-1.5">
                <Leaf className="w-4 h-4"/>
                Экологический эквивалент
              </h3>
              <div className="text-sm text-lime-600 dark:text-lime-400">
                <p>
                  Ваши выбросы CO₂ эквивалентны:
                </p>
                <ul className="list-disc pl-5 mt-1 space-y-1 text-xs">
                  <li>{formatNumber(results.co2Emissions / 10, 1)} деревьев в год для поглощения</li>
                  <li>{formatNumber(results.co2Emissions * 4.2, 1)} км поездки на общественном транспорте</li>
                  {results.co2Emissions > 50 && (
                    <li>{formatNumber(results.co2Emissions / 100, 1)} среднемесячных выбросов от человека</li>
                  )}
                </ul>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Расстояние" 
          value={`${formatNumber(results.distance, 1)} ${results.distanceUnit === 'miles' ? 'миль' : 'км'}`} 
          color="blue" 
          icon={<Ruler className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Расход топлива" 
          value={`${formatNumber(results.fuelConsumptionWithEco, 1)} ${getConsumptionUnit()}`} 
          color="blue" 
          icon={<Droplet className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Тип топлива" 
          value={results.fuelType?.name || ''} 
          icon={<Car className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Количество топлива" 
          value={`${formatNumber(results.fuelAmount, 1)} ${getFuelAmountUnit()}`} 
          icon={<Truck className="w-4 h-4"/>} 
        />

        <ResultItem 
          label="Стоимость на 100 км" 
          value={formatCurrency(results.costPer100km)}
          color="green" 
        />
        
        <ResultItem 
          label="Стоимость на 1 км" 
          value={`${formatNumber(results.costPerKm, 2)} ₽/км`} 
          color="green" 
        />
        
        <ResultItem 
          label="Выбросы CO₂" 
          value={`${formatNumber(results.co2Emissions, 1)} кг (${formatNumber(results.co2EmissionsPerKm, 2)} кг/км)`} 
          color="amber" 
          icon={<Leaf className="w-4 h-4"/>} 
        />

        {results.ecoSavingsLiters > 0 && (
          <ResultItem 
            label="Экономия от эко-режимов" 
            value={`${formatNumber(results.ecoSavingsLiters, 1)} л/100км (${formatNumber(results.ecoSavingsPercent, 0)}%)`} 
            color="green" 
            icon={<Leaf className="w-4 h-4"/>} 
          />
        )}

        {results.rangeOnFullTank > 0 && (
          <ResultItem 
            label="Запас хода на полном баке" 
            value={`${formatNumber(results.rangeOnFullTank, 0)} км`} 
            color="blue" 
            icon={<Gauge className="w-4 h-4"/>} 
          />
        )}
        
        {results.tripTimeHours > 0 && (
          <ResultItem 
            label="Время в пути" 
            value={`${formatNumber(results.tripTimeHours, 1)} ч (${formatNumber(results.tripTimeMinutes, 0)} мин)`} 
            icon={<Clock className="w-4 h-4"/>} 
          />
        )}

        {/* Отображение данных автомобиля, если они указаны */}
        {(results.vehicleMake || results.vehicleModel) && (
          <ResultItem 
            label="Автомобиль" 
            value={`${results.vehicleMake} ${results.vehicleModel} ${results.vehicleYear || ''} ${results.engineVolume ? `(${results.engineVolume} л)` : ''}`} 
            icon={<Car className="w-4 h-4"/>} 
          />
        )}
      </div>

      {/* Информация о расчете */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <Gauge className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Точность расчетов</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Расчет основан на указанных вами параметрах и средних показателях расхода топлива в России. 
              Фактический расход может варьироваться в зависимости от стиля вождения, погодных условий, 
              загруженности дорог и технического состояния автомобиля.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;